import { PDM_PART_ATTRIBUTES } from "../constants/pdm-attribute-keys"
import type { SeatDiagramLabelContext } from "../constants/seat-diagram-schematic-labels"
import { formatVehicleModelSeatDiagramLabel } from "../constants/seat-diagram-schematic-labels"

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v)
}

export function unwrapValue(field: unknown): string {
  if (field === null || field === undefined) {
    return ""
  }
  if (typeof field === "string" || typeof field === "number") {
    return String(field).trim()
  }
  if (isPlainRecord(field) && typeof field.value === "string") {
    return field.value.trim()
  }
  if (
    isPlainRecord(field) &&
    (typeof field.label === "string" || typeof field.name === "string")
  ) {
    return (
      typeof field.label === "string" ? field.label : (field.name as string)
    ).trim()
  }
  return ""
}

export function normalizeString(a: unknown, b: unknown): boolean {
  return unwrapValue(a).toLowerCase() === unwrapValue(b).toLowerCase()
}

/** `applications[].years` may be an array of years or comma-separated ranges — best-effort. */
export function yearIncludedInApplication(
  yearsField: unknown,
  selectedYear: number
): boolean {
  if (yearsField === null || yearsField === undefined) {
    return true
  }
  if (Array.isArray(yearsField)) {
    return yearsField.some(
      (y) => Number(y) === selectedYear || String(y) === String(selectedYear)
    )
  }
  if (typeof yearsField === "string") {
    const s = yearsField.trim()
    if (/^\d{4}-\d{4}$/.test(s)) {
      const [from, to] = s.split("-").map((x) => Number(x))
      return selectedYear >= from && selectedYear <= to
    }
    const parts = s
      .split(/[\s,/]+/)
      .map((p) => p.trim())
      .filter(Boolean)
    return parts.some(
      (p) => Number(p) === selectedYear || p === String(selectedYear)
    )
  }
  const n = Number(yearsField)
  if (!Number.isNaN(n)) {
    return n === selectedYear
  }
  return true
}

export function getApplications(
  product: Record<string, unknown>
): Record<string, unknown>[] {
  const apps = product.applications ?? product.Applications
  if (!Array.isArray(apps)) {
    return []
  }
  return apps.filter(isPlainRecord) as Record<string, unknown>[]
}

export type PartAttrsMap = Map<string, string>

/**
 * Normalize `products[].part_attributes[]` keyed by lowercase attribute → joined attribute_value(s).
 */
export function normalizePartAttrs(
  product: Record<string, unknown>
): PartAttrsMap {
  const bucket = new Map<string, Set<string>>()
  const raw = product.part_attributes ?? product.partAttributes

  if (!Array.isArray(raw)) {
    return new Map()
  }

  for (const row of raw) {
    if (!isPlainRecord(row)) continue

    const nameRaw =
      (typeof row.attribute === "string" && row.attribute) ||
      (typeof row.Attribute === "string" && row.Attribute) ||
      ""
    const key = nameRaw.trim().toLowerCase()

    const valRaw =
      (typeof row.attribute_value === "string" && row.attribute_value.trim()) ||
      (typeof row.attributeValue === "string" && row.attributeValue.trim()) ||
      unwrapValue(row.value)

    const val = (valRaw || "").trim()

    if (!key || !val) continue

    if (!bucket.has(key)) bucket.set(key, new Set<string>())
    bucket.get(key)!.add(val)
  }

  const out = new Map<string, string>()
  bucket.forEach((set, nm) => {
    out.set(
      nm,
      Array.from(set.values())
        .sort((a, b) => a.localeCompare(b))
        .join(", ")
    )
  })
  return out
}

export function attrValue(attrs: PartAttrsMap, canonicalName: string): string {
  return attrs.get(canonicalName.toLowerCase())?.trim() || ""
}

export function getSeatAndRowSlices(product: Record<string, unknown>): {
  frontRow: string
  backRow: string
  middleRow: string
  driverSeat: string
  passengerSeat: string
} {
  const attrs = normalizePartAttrs(product)
  return {
    frontRow: attrValue(attrs, PDM_PART_ATTRIBUTES.FRONT_ROW),
    backRow: attrValue(attrs, PDM_PART_ATTRIBUTES.BACK_ROW),
    middleRow: attrValue(attrs, PDM_PART_ATTRIBUTES.MIDDLE_ROW),
    driverSeat: attrValue(attrs, PDM_PART_ATTRIBUTES.DRIVER_SEAT_TYPE),
    passengerSeat: attrValue(attrs, PDM_PART_ATTRIBUTES.PASSENGER_SEAT_TYPE),
  }
}

export function getSeatingRowValue(product: Record<string, unknown>): string {
  const attrs = normalizePartAttrs(product)
  return attrValue(attrs, PDM_PART_ATTRIBUTES.SEATING_ROW)
}

/** Raw PDM `Inventory` attribute value (`Yes`, `No`, or empty). */
export function getPdmInventoryValue(product: Record<string, unknown>): string {
  const attrs = normalizePartAttrs(product)
  return attrValue(attrs, PDM_PART_ATTRIBUTES.INVENTORY)
}

/** True only when PDM `Inventory` is `Yes` (case-insensitive). */
export function isPdmInventoryInStock(
  product: Record<string, unknown>
): boolean {
  const raw = getPdmInventoryValue(product).toLowerCase().trim()
  return raw === "yes"
}

/**
 * Map PDM `part_number` → stocking label for Inventory Checker.
 * Uses `flattenMatchingApplications` so only kits for the selected YMM are included.
 */
export function buildPdmStockingStatusMap(
  products: Record<string, unknown>[],
  make: string,
  model: string,
  year: string
): Record<string, string> {
  const yearNum = Number.parseInt(year, 10)
  if (!Number.isFinite(yearNum) || !products.length) {
    return {}
  }

  const flatRows = flattenMatchingApplications(products, make, model, yearNum)
  const out: Record<string, string> = {}

  for (const row of flatRows) {
    const partNumber = unwrapValue(
      row.product.part_number ?? row.product.PartNumber
    )
    if (!partNumber || out[partNumber]) continue

    const inStock = isPdmInventoryInStock(row.product)
    out[partNumber] = inStock ? "In Stock" : "Out of Stock"
  }

  return out
}

export function shouldHideSingleRowProduct(
  product: Record<string, unknown>
): boolean {
  return normalizeString(getSeatingRowValue(product), "1 Row")
}

export interface PdmFlatRow {
  product: Record<string, unknown>
  application: Record<string, unknown>
}

/**
 * Rows where applications match selected make/model/year (trim + case-insensitive make/model).
 * One row per qualifying `applications[]` entry (ties part_attributes + application together).
 */
export function flattenMatchingApplications(
  products: Record<string, unknown>[],
  make: string,
  model: string,
  selectedYearNum: number
): PdmFlatRow[] {
  const out: PdmFlatRow[] = []
  const wantMake = unwrapValue(make)
  const wantModel = unwrapValue(model)

  for (const product of products) {
    // Skip products that are not active
    if (!isProductActive(product)) {
      continue
    }
    if (shouldHideSingleRowProduct(product)) {
      continue
    }

    for (const app of getApplications(product)) {
      if (!normalizeString(app.make, wantMake)) {
        continue
      }

      if (!normalizeString(app.model, wantModel)) {
        continue
      }

      if (!yearIncludedInApplication(app.years, selectedYearNum)) {
        continue
      }

      out.push({ product, application: app })
    }
  }

  return out
}

/**
 * Check if a product is active and should be displayed.
 * Only products with population_status "Active (Publish to receiver database)" are shown.
 */
export function isProductActive(product: Record<string, unknown>): boolean {
  const status = unwrapValue(
    product.population_status ?? product.populationStatus ?? ""
  )

  return /\bActive\b/i.test(status)
}

export function distinctSorted(values: Iterable<string>): string[] {
  const seen = new Set<string>()
  Array.from(values).forEach((v) => {
    const t = v.trim()
    if (t !== "") seen.add(t)
  })
  return Array.from(seen).sort((a, b) => a.localeCompare(b))
}

/**
 * Extract image filename from PDM product's `digital_assets[]` array.
 * Returns the first digital asset's `file_name` or empty string if not found.
 *
 * PDM structure:
 * ```
 * products[].digital_assets[] = [
 *   {
 *     file_name: "CHE2305X-SD.png",
 *     file_type_code: "PNG",
 *     hash_ref: "...",
 *     height: 2000,
 *     width: 2000
 *   }
 * ]
 * ```
 */
export function extractProductImage(product: Record<string, unknown>): string {
  const digitalAssets = product.digital_assets ?? product.digitalAssets

  if (!Array.isArray(digitalAssets) || digitalAssets.length === 0) {
    return ""
  }

  const firstAsset = digitalAssets[0]
  if (!isPlainRecord(firstAsset)) {
    return ""
  }

  const fileName = unwrapValue(firstAsset.file_name ?? firstAsset.fileName)
  return fileName || ""
}

/**
 * Extract digital asset details from PDM product for factory match seat diagram.
 * Returns image filename, hash, and asset type from first digital asset.
 *
 * PDM structure:
 * ```
 * products[].digital_assets[] = [
 *   {
 *     file_name: "CHE2305X-SD.png",
 *     hash_ref: "abc123...",
 *     digital_asset_type_code: "SD"
 *   }
 * ]
 * ```
 */
export function extractDigitalAssetDetails(product: Record<string, unknown>): {
  imageFilename: string
  imageHash: string
  assetType: string
} {
  const digitalAssets = product.digital_assets ?? product.digitalAssets

  if (!Array.isArray(digitalAssets) || digitalAssets.length === 0) {
    return { imageFilename: "", imageHash: "", assetType: "" }
  }

  const firstAsset = digitalAssets[0]
  if (!isPlainRecord(firstAsset)) {
    return { imageFilename: "", imageHash: "", assetType: "" }
  }

  return {
    imageFilename: unwrapValue(firstAsset.file_name ?? firstAsset.fileName),
    imageHash: unwrapValue(firstAsset.hash_ref ?? firstAsset.hashRef),
    assetType: unwrapValue(
      firstAsset.digital_asset_type_code ?? firstAsset.digitalAssetTypeCode
    ),
  }
}

/**
 * Remove "AT" prefix from stitching color codes.
 * Example: "AT626C-Charcoal Grey" becomes "626C-Charcoal Grey"
 */
export function normalizeStitchingColor(color: string): string {
  if (!color) return ""
  return color.trim().replace(/^AT/i, "").trim()
}

/**
 * Subtext for seat diagram schematic tiles (factory match cards).
 * Uses selected YMM from Dato + display mode (from legacy Dato factory-match samples).
 */
export function formatSeatDiagramSchematicLabel(
  application: Record<string, unknown>,
  productTitle = "",
  product?: Record<string, unknown>,
  /** Rows-step label when PDM part_attributes omit Back/Front Row (e.g. "60-40"). */
  rowLabelHint = "",
  labelContext?: SeatDiagramLabelContext
): string {
  const make =
    labelContext?.selectedMake?.trim() ||
    unwrapValue(application.make ?? application.Make)
  const model =
    labelContext?.selectedModel?.trim() ||
    unwrapValue(application.model ?? application.Model)
  const displayMode = labelContext?.displayMode ?? "row-schematic"

  if (displayMode === "vehicle-model") {
    const vehicleLabel = formatVehicleModelSeatDiagramLabel(make, model)
    if (vehicleLabel) {
      return vehicleLabel
    }
  }

  const hinted = rowLabelHint.trim()
  if (product || hinted) {
    const { backRow, middleRow, frontRow } = product
      ? getSeatAndRowSlices(product)
      : { backRow: "", middleRow: "", frontRow: "" }
    const rowValue = hinted || backRow || middleRow || frontRow
    if (rowValue) {
      if (/schematic/i.test(rowValue)) {
        return rowValue
      }
      return `${rowValue} Schematic`
    }
  }

  const vehicleFallback = formatVehicleModelSeatDiagramLabel(make, model)
  if (vehicleFallback) {
    return vehicleFallback
  }
  return productTitle
}

/** Dato kit step skus embedded in `part_number` immediately after `base_part_number`. */
const FACTORY_MATCH_KIT_TIER_BY_CODE: Record<string, string> = {
  XS: "Standard",
  XP: "Premium",
  XF: "Full Leather",
}

/**
 * Kit tile subtext: Standard / Premium / Full Leather from part number (XS / XP / XF),
 * not PDM `Kit Type` (e.g. "2 Row").
 */
export function resolveFactoryMatchKitDisplayValue(
  partNumber: string,
  basePartNumber = ""
): string {
  const pn = partNumber.trim().toUpperCase()
  const base = basePartNumber.trim().toUpperCase()
  const suffix =
    base.length > 0 && pn.startsWith(base) ? pn.slice(base.length) : pn
  const tierSuffix = suffix.startsWith("N") ? suffix.slice(1) : suffix

  for (const code of ["XF", "XP", "XS"] as const) {
    if (tierSuffix.startsWith(code)) {
      return FACTORY_MATCH_KIT_TIER_BY_CODE[code]
    }
  }
  return ""
}

function formatCodeDashLabel(raw: string, separator: string): string {
  const v = raw.trim()
  if (!v) return ""
  const dash = v.indexOf("-")
  if (dash === -1) return v
  const code = v.slice(0, dash).trim()
  const label = v
    .slice(dash + 1)
    .trim()
    .replace(/_/g, " ")
  if (!code || !label) return v
  return `${code.toLowerCase()}${separator}${label.toLowerCase()}`
}

/** e.g. `ML0501-BLACK` → `ml0501 black` */
export function formatFactoryMatchColorDisplay(raw: string): string {
  return formatCodeDashLabel(raw, " ")
}

/** e.g. `626C-CHARCOAL GREY` → `626c - charcoal grey` */
export function formatFactoryMatchStitchDisplay(raw: string): string {
  return formatCodeDashLabel(raw, " - ")
}

/** Factory-match design defaults (not in PDM part_attributes). */
export const FACTORY_MATCH_PATTERN_DEFAULT = "Factory pattern"
export const FACTORY_MATCH_TONE_DEFAULT = "Single tone"

/**
 * Extract factory match attributes from PDM product.
 * Returns Kit Type, Colors, and other factory match specific attributes.
 */
export function extractFactoryMatchAttributes(
  product: Record<string, unknown>
): {
  kit: string
  pattern: string
  tone: string
  perforation: string
  primaryColor: string
  insertColor: string
  stitch: string
} {
  const attrs = normalizePartAttrs(product)

  return {
    kit: attrValue(attrs, PDM_PART_ATTRIBUTES.KIT_TYPE),
    pattern: "", // Not in PDM per mapping
    tone: "", // Not in PDM per mapping
    perforation: attrValue(attrs, PDM_PART_ATTRIBUTES.PERFORATION_LOCATION),
    primaryColor: attrValue(attrs, PDM_PART_ATTRIBUTES.PRIMARY_COLOR),
    insertColor: attrValue(attrs, PDM_PART_ATTRIBUTES.SECONDARY_COLOR),
    stitch: attrValue(attrs, PDM_PART_ATTRIBUTES.STITCHING_COLOR),
  }
}
